import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  MoveToInboxOutlined,
  VisibilityOutlined,
  StarBorderOutlined,
  AssessmentOutlined,
  PrintOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import { downloadBase64Pdf, downloadBase64Zip } from "../../utils/downloadPdf";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { PrintPdfDialog } from "../../components/printPdfDialog/PrintPdfDialog";
import { PrintCollectorsPdfDialog } from "../../components/printPdfDialog/PrintCollectorsPdfDialog";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICep } from "../../interfaces/ICep";
import { IExhibition } from "../../interfaces/IExhibition";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import { IState } from "../../interfaces/IState";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";
import { ExhibitionJudges } from "./judges/ExhibitionJudges";
import { Judgments } from "./judgments/Judgments";

export const Exhibition: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut, user } = useAuth();

  const [exhibitionId, setExhibitionId] = useState("");
  const [name, setName] = useState("");
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [details, setDetails] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("");
  const [statesOpions, setStatesOptions] = useState<IOption[]>([]);
  const [stateOption, setStateOption] = useState<IOption | null>(null);
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [address, setAddress] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [addressReference, setAddressReference] = useState("");
  const [president, setPresident] = useState("");
  const [coordinator, setCoordinator] = useState("");
  const [exhibitionFinished, setExhibitionFinished] = useState(false);
  const [finished, setFinished] = useState(false);
  const [allowRegistration, setAllowRegistration] = useState(false);

  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printCollectorsDialogOpen, setPrintCollectorsDialogOpen] =
    useState(false);
  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const stateToOption = (states: IState): IOption => {
    return {
      value: `${states.id}`,
      description: `${states.id} - ${states.name}`,
    };
  };

  const handleSetState = (value: string | null) => {
    if (value) {
      const selected = statesOpions.find(states => {
        return states.value === value;
      });

      if (selected?.value) {
        setStateOption(selected);
      } else {
        setStateOption(null);
      }

      setZipCode("");
      setCity("");
      setNeighborhood("");
      setAddress("");
      setAddressNumber("");
      setAddressComplement("");
      setAddressReference("");
    }
  };

  const handleResquestStates = useCallback(async () => {
    setLoading(true);

    await api
      .get(`/states`)
      .then(response => {
        const states: IState[] = response.data;

        const statesOptions = states.map(stateToOption);
        setStatesOptions(statesOptions);

        const selected = statesOptions.find(states => {
          return states.value === stateOption?.value;
        });

        if (selected?.value) {
          setStateOption(selected);
        }
        setStateOption(null);
      })
      .catch(error => {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }

        toastr.error(
          error.response.data?.message || "Contate a equipe de suporte",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [stateOption?.value, navigate, signOut, toastr]);

  const handleResquestZipCode = useCallback(async () => {
    if (zipCode.length === 9) {
      setLoading(true);

      await api
        .get(`/cep/${zipCode}`)
        .then(response => {
          if (response.data.erro) {
            toastr.error("CEP não encontrado");
            return;
          }

          const cep: ICep = response.data;

          setCountry("Brasil");
          setCity(cep.localidade);
          setNeighborhood(cep.bairro);
          setAddress(cep.logradouro);
          setAddressNumber("");
          setAddressComplement("");
          setAddressReference("");

          const state = statesOpions.find(state => {
            return state.value.toUpperCase() === cep.uf.toUpperCase();
          });

          if (state) {
            setStateOption(state);
          } else {
            setStateOption(null);
          }
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [zipCode, statesOpions, navigate, signOut, toastr]);

  const handleListExhibition = useCallback(async () => {
    const id = location.pathname.replace("/exhibition", "").replace("/", "");

    await handleResquestStates();

    if (id) {
      setExhibitionId(id);
      setLoading(true);

      await api
        .get(`/exhibitions/${id}`)
        .then(response => {
          const exhibition: IExhibition = response.data;

          setName(exhibition.name ? exhibition.name : "");
          exhibition.date ? setDate(dayjs(exhibition.date)) : setDate(null);
          setZipCode(exhibition.zipCode ? exhibition.zipCode : "");
          setCountry(exhibition.country ? exhibition.country : "");
          setCity(exhibition.city ? exhibition.city : "");
          setNeighborhood(
            exhibition.neighborhood ? exhibition.neighborhood : "",
          );
          setAddress(exhibition.address ? exhibition.address : "");
          setAddressNumber(
            exhibition.addressNumber ? exhibition.addressNumber : "",
          );
          setAddressComplement(
            exhibition.addressComplement ? exhibition.addressComplement : "",
          );
          setAddressReference(
            exhibition.addressReference ? exhibition.addressReference : "",
          );
          setDetails(exhibition.details ? exhibition.details : "");
          setPresident(exhibition.president ? exhibition.president : "");
          setCoordinator(exhibition.coordinator ? exhibition.coordinator : "");

          if (exhibition.state) {
            setStateOption(stateToOption(exhibition.state));
          }
          setFinished(exhibition.finished ? exhibition.finished : false);
          setExhibitionFinished(
            exhibition.finished ? exhibition.finished : false,
          );
          setAllowRegistration(
            exhibition.allowRegistration ? exhibition.allowRegistration : false,
          );
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname, handleResquestStates]);

  const handleDeleteExhibition = useCallback(async () => {
    const id = location.pathname.replace("/exhibition", "").replace("/", "");

    if (id) {
      setLoading(true);

      await api
        .delete(`/exhibitions/${id}`)
        .then(() => {
          navigate("/exhibitions");
          toastr.success("Exposição excluída com sucesso");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const handleDownloadResultsReport = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const utcOffset = new Date().getTimezoneOffset() * -1;
        const response = await api.get(
          `/exhibitions/${id}/results/report?utcOffset=${utcOffset}`,
        );
        const { filename, base64 } = response.data;
        downloadBase64Pdf(base64, filename);
      } catch (error: any) {
        if (error?.response?.status === 422) {
          toastr.warning(
            "Execute a consolidação antes de gerar o relatório. Marque a exposição como Finalizada e confirme para consolidar.",
          );
        } else {
          toastr.error(
            error?.response?.data?.message ||
              "Erro ao gerar relatório de resultados",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [toastr],
  );

  const handleGenerateJudgesCertificates = useCallback(async () => {
    if (!exhibitionId) return;
    setLoading(true);
    try {
      const response = await api.post(
        `/exhibitions/${exhibitionId}/evaluation/judges/certificates`,
        { judgeIds: [] },
      );
      const { zip, certificates } = response.data as {
        zip?: { filename: string; base64: string };
        certificates?: { filename: string; base64: string }[];
      };
      if (zip) {
        downloadBase64Zip(zip.base64, zip.filename);
        toastr.success("Certificados dos juízes gerados com sucesso!");
      } else if (certificates && certificates.length > 0) {
        certificates.forEach(c => {
          downloadBase64Pdf(c.base64, c.filename);
        });
        toastr.success("Certificados dos juízes gerados com sucesso!");
      } else {
        toastr.error("Nenhum certificado foi retornado");
      }
    } catch (error: any) {
      toastr.error(
        error?.response?.data?.message ||
          "Erro ao gerar certificados dos juízes",
      );
    } finally {
      setLoading(false);
    }
  }, [exhibitionId, toastr]);

  const handleDuplicateExhibition = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await api.post(`/exhibitions/${id}/duplicate`);
        toastr.success("Exposição duplicada com sucesso!");
        window.location.href = "/exhibition/" + response.data.id;
      } catch (error: any) {
        toastr.error(
          error?.response?.data?.message || "Erro ao duplicar exposição",
        );
      } finally {
        setLoading(false);
      }
    },
    [toastr],
  );

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});
      setLoading(true);
      if (exhibitionId) {
        const data: IExhibition = {
          name,
          details,
          zipCode,
          country,
          stateId: stateOption?.value || "",
          city,
          neighborhood,
          address,
          addressNumber,
          addressComplement,
          addressReference,
          date: date ? `${dayjs(date).format("YYYY-MM-DD")}15:00:00.000` : null,
          president,
          coordinator,
          finished,
          allowRegistration,
        };

        const schema = Yup.object().shape({
          name: Yup.string().required("Name é obrigatório"),
          city: Yup.string().required("Cidade é obrigatória"),
          stateId: Yup.string().required("Estado é obrigatório"),
          date: Yup.string().required("Data é obrigatória"),
          finished: Yup.boolean(),
          allowRegistration: Yup.boolean(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/exhibitions/${exhibitionId}`, data)
          .then(async () => {
            if (!exhibitionFinished && finished === true) {
              try {
                const consolidation = await api.post(
                  `/exhibitions/${exhibitionId}/consolidate-results`,
                );
                await handleListExhibition();
                toastr.success(
                  `Exposição finalizada! ${consolidation.data.totalPlants} plantas tiveram seus resultados calculados.`,
                );
              } catch {
                await handleListExhibition();
                toastr.warning(
                  "Exposição salva, mas a consolidação de resultados falhou. Tente novamente pelo menu da exposição.",
                );
              }
            } else {
              await handleListExhibition();
              toastr.success("Exposição atualizada com sucesso");
            }
            navigate("/exhibition/" + exhibitionId);
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const data: IExhibition = {
          name,
          details,
          zipCode,
          country,
          stateId: stateOption?.value || "",
          city,
          neighborhood,
          address,
          addressNumber,
          addressComplement,
          addressReference,
          date: date ? `${dayjs(date).format("YYYY-MM-DD")}15:00:00.000` : null,
          president,
          coordinator,
          allowRegistration,
        };

        const schema = Yup.object().shape({
          name: Yup.string().required("Name é obrigatório"),
          city: Yup.string().required("Cidade é obrigatória"),
          stateId: Yup.string().required("Estado é obrigatório"),
          date: Yup.string().required("Data é obrigatória"),
          allowRegistration: Yup.boolean(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/exhibitions`, data)
          .then(response => {
            toastr.success("Exposição cadastrada com sucesso");
            if (response.data.id) {
              setExhibitionId(response.data.id);
              navigate("/exhibition/" + response.data.id);
            }
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(
        error.response.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [
    exhibitionId,
    name,
    details,
    zipCode,
    country,
    stateOption,
    city,
    neighborhood,
    address,
    addressNumber,
    addressComplement,
    addressReference,
    date,
    president,
    coordinator,
    finished,
    allowRegistration,
    navigate,
    signOut,
    toastr,
    handleListExhibition,
    exhibitionFinished,
  ]);

  const handleChangeFinished = (event: ChangeEvent<HTMLInputElement>) => {
    setFinished(event.target.checked);
  };

  const handleChangeAllowRegistration = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setAllowRegistration(event.target.checked);
  };

  useEffect(() => {
    handleListExhibition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Exposição</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Nome"
                value={name}
                required
                autoFocus
                onChange={e => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DatePicker
                  sx={{ width: "100%" }}
                  slotProps={{ textField: { size: "small" } }}
                  label={`Data *`}
                  value={date}
                  onChange={value => setDate(dayjs(String(value)))}
                  disabled={exhibitionFinished}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Observação"
                value={details}
                onChange={e => setDetails(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"99999-999"}
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                maskPlaceholder={null}
                onBlur={handleResquestZipCode}
                disabled={exhibitionFinished}
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="CEP"
                  helperText={formErrors.zipCode}
                  error={!!formErrors.zipCode}
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="País"
                value={country}
                onChange={e => setCountry(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl fullWidth size="small" error={!!formErrors.stateId}>
                <InputLabel
                  id="select-filter"
                  error={!!formErrors.stateId}
                  required
                >
                  Estado
                </InputLabel>
                <Select
                  label="Estado"
                  value={stateOption?.value || ""}
                  onChange={e => handleSetState(String(e.target.value))}
                  error={!!formErrors.stateId}
                  disabled={exhibitionFinished}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 5.5, // 5 itens visíveis (aprox. 48px cada)
                      },
                    },
                  }}
                  required
                >
                  {statesOpions.map(s => {
                    return (
                      <MenuItem key={s.description} value={s.value}>
                        {s.description}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{formErrors.stateId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Cidade"
                value={city}
                required
                onChange={e => setCity(e.target.value)}
                error={!!formErrors.city}
                helperText={formErrors.city}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Bairro"
                value={neighborhood}
                onChange={e => setNeighborhood(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Endereço"
                value={address}
                onChange={e => setAddress(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Número"
                value={addressNumber}
                onChange={e => setAddressNumber(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Complemento"
                value={addressComplement}
                onChange={e => setAddressComplement(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Referência"
                value={addressReference}
                onChange={e => setAddressReference(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Presidente"
                value={president}
                onChange={e => setPresident(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Coordenador"
                value={coordinator}
                onChange={e => setCoordinator(e.target.value)}
                disabled={exhibitionFinished}
              />
            </Grid>

            {exhibitionId && (
              <Grid item xs={12} md={6} sm={6}>
                <FormControlLabel
                  label="Finalizado"
                  control={
                    <Checkbox
                      checked={finished}
                      onChange={handleChangeFinished}
                    />
                  }
                />
              </Grid>
            )}

            <Grid item xs={12} md={6} sm={6}>
              <FormControlLabel
                label="Permitir Inscrição"
                control={
                  <Checkbox
                    checked={allowRegistration}
                    onChange={handleChangeAllowRegistration}
                  />
                }
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              mt: "32px",
            }}
          >
            {/* Linha 1: Gerenciamento da Exposição */}
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <ButtonTheme
                type="button"
                onClick={() => navigate("/exhibitions")}
                variant="contained"
                color="inherit"
                disabled={loading}
              >
                Voltar
              </ButtonTheme>

              {exhibitionId && !exhibitionFinished && (
                <ButtonTheme
                  type="button"
                  onClick={() => setDialogDeleteOpen(true)}
                  variant="contained"
                  color="error"
                  disabled={loading}
                >
                  Excluir
                </ButtonTheme>
              )}

              {exhibitionId && (
                <ButtonTheme
                  type="button"
                  onClick={() => handleDuplicateExhibition(exhibitionId)}
                  variant="contained"
                  color="info"
                  disabled={loading}
                >
                  Duplicar
                </ButtonTheme>
              )}

              <ButtonTheme
                onClick={handleSubmit}
                variant="contained"
                color="success"
                disabled={loading}
              >
                Confirmar
              </ButtonTheme>
            </Box>

            {/* Linha 2: Operações e Relatórios (Apenas se a exposição já existir) */}
            {exhibitionId && (
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  pt: "16px",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                {allowRegistration && (
                  <ButtonTheme
                    type="button"
                    onClick={() =>
                      navigate(`/received/exhibition/${exhibitionId}`)
                    }
                    variant="outlined"
                    disabled={loading}
                    startIcon={<MoveToInboxOutlined />}
                  >
                    Recebimento
                  </ButtonTheme>
                )}

                <ButtonTheme
                  type="button"
                  onClick={() =>
                    window.open(`/follow-up/${exhibitionId}`, "_blank")
                  }
                  variant="outlined"
                  disabled={loading}
                  startIcon={<VisibilityOutlined />}
                >
                  Acompanhar
                </ButtonTheme>

                {exhibitionFinished && (
                  <ButtonTheme
                    type="button"
                    onClick={() =>
                      window.open(`/best-collectors/${exhibitionId}`, "_blank")
                    }
                    variant="outlined"
                    disabled={loading}
                    startIcon={<StarBorderOutlined />}
                  >
                    Melhores Colecionadores
                  </ButtonTheme>
                )}

                {user?.typeId === 1 && exhibitionFinished && (
                  <ButtonTheme
                    type="button"
                    onClick={handleGenerateJudgesCertificates}
                    variant="outlined"
                    disabled={loading}
                    startIcon={<WorkspacePremiumOutlined />}
                  >
                    Certificado Juizes
                  </ButtonTheme>
                )}

                {exhibitionFinished && (
                  <ButtonTheme
                    type="button"
                    onClick={() =>
                      window.open(`/results/${exhibitionId}`, "_blank")
                    }
                    variant="outlined"
                    disabled={loading}
                    startIcon={<AssessmentOutlined />}
                  >
                    Resultados
                  </ButtonTheme>
                )}

                <ButtonTheme
                  type="button"
                  onClick={() => setPrintDialogOpen(true)}
                  variant="outlined"
                  color="success"
                  disabled={loading}
                  startIcon={<PrintOutlined />}
                >
                  Relat. Plantas
                </ButtonTheme>

                <ButtonTheme
                  type="button"
                  onClick={() => setPrintCollectorsDialogOpen(true)}
                  variant="outlined"
                  color="success"
                  disabled={loading}
                  startIcon={<PrintOutlined />}
                >
                  Relat. Colecionadores
                </ButtonTheme>

                {exhibitionFinished && (
                  <ButtonTheme
                    type="button"
                    onClick={() => handleDownloadResultsReport(exhibitionId)}
                    variant="outlined"
                    color="success"
                    disabled={loading}
                    startIcon={<PrintOutlined />}
                  >
                    Relat. Resultados
                  </ButtonTheme>
                )}
              </Box>
            )}
          </Box>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={exhibitionId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${exhibitionId}`}
          handleConfirmation={handleDeleteExhibition}
          deny={() => setDialogDeleteOpen(false)}
        />

        {exhibitionId && (
          <PrintPdfDialog
            open={printDialogOpen}
            onClose={() => setPrintDialogOpen(false)}
            exhibitionId={exhibitionId}
            allowSplit
            mode="download"
          />
        )}

        {exhibitionId && (
          <PrintCollectorsPdfDialog
            open={printCollectorsDialogOpen}
            onClose={() => setPrintCollectorsDialogOpen(false)}
            exhibitionId={exhibitionId}
          />
        )}

        {loading && <BackdropCustom />}
      </PageCard>

      {exhibitionId && (
        <Judgments exhibitionId={exhibitionId} finished={exhibitionFinished} />
      )}

      {exhibitionId && (
        <ExhibitionJudges
          exhibitionId={exhibitionId}
          finished={exhibitionFinished}
        />
      )}
    </>
  );
};
